import { type Theme, type ThemeOptions } from '@mui/material/styles';

/** Desktop dialog paper + mobile bottom-sheet look (see BottomSheet.tsx). */
export function dialogThemeComponents(theme: Theme): ThemeOptions['components'] {
  return {
    MuiPaper: {
      styleOverrides: {
        root: {
          // White on desktop too (theme's background.paper is grey), matching the mobile sheet.
          '&.SmDialog-paper': {
            backgroundColor: theme.palette.common.white
          },
          // Same as MUI's max height, so the content (grid / list) gets a stable height to fill.
          '&.SmDialog-fullHeight': {
            height: 'calc(100% - 64px)'
          },
          '&.SmDialog-allowOverflow, &.SmDialog-allowOverflow .MuiDialogContent-root': {
            overflow: 'visible'
          },
          // A <form> wrapping DialogContent + DialogActions: lay it out like the paper itself.
          '&.SmDialog-paper > form': {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: '1 1 auto'
          },
          // X at the inline end of the title row (desktop; the sheet renders no X).
          '& .SmDialog-close': {
            position: 'absolute',
            top: theme.spacing(1.5),
            insetInlineEnd: theme.spacing(1.5),
            zIndex: 1
          },
          '& .SmDialog-title-withClose': {
            paddingInlineEnd: theme.spacing(8)
          },
          // No title: keep the body clear of the X (desktop only — the sheet has no X). `uniform` opts out:
          // same padding on every side, the X floats over the corner.
          '&.SmDialog-paper .SmDialog-close + .SmDialog-content:not(.SmDialog-content-flush):not(.SmDialog-content-uniform), &.SmDialog-paper > form > .SmDialog-close + .SmDialog-content:not(.SmDialog-content-flush):not(.SmDialog-content-uniform)':
            {
              paddingTop: theme.spacing(7)
            },
          // `uniform`: 24px on every side, like the sides of a regular body.
          '& .SmDialog-content-uniform': {
            padding: theme.spacing(3)
          },
          '& .SmDialog-content-flush': {
            padding: 0
          },
          // Title and action row are separated from the body by a divider (desktop and mobile sheet).
          '& .SmDialog-title': {
            borderBottom: `1px solid ${theme.palette.divider}`
          },
          // MUI drops the body's top padding right after a title — restore it so the body clears the divider.
          '& .SmDialog-title + .SmDialog-content:not(.SmDialog-content-flush)': {
            paddingTop: theme.spacing(2)
          },
          // Action row: cancel first, primary last (the order is fixed by Dialog).
          '& .SmDialog-actions': {
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: theme.spacing(1),
            padding: theme.spacing(1.5, 3, 2),
            '& > *': { margin: 0 }
          },
          // Mobile: buttons spread across the row; a lone button sits at the end.
          '&.SmBottomSheet-paper .SmDialog-actions': {
            justifyContent: 'space-between',
            '& > :only-child': { marginInlineStart: 'auto' }
          },
          '&.SmBottomSheet-paper': {
            position: 'absolute',
            insetInline: 0,
            bottom: 0,
            // Fits its content, capped at 80% (then the body scrolls). Keep in sync with COLLAPSED_RATIO in useSheetDrag.ts.
            maxHeight: '80%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.common.white,
            borderRadius: '22px 22px 0 0',
            outline: 'none',
            overflow: 'hidden',
            paddingBottom: 'env(safe-area-inset-bottom)'
          },
          '&.SmBottomSheet-expanded': {
            height: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            paddingTop: 'env(safe-area-inset-top)'
          },
          '& .SmBottomSheet-header': {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '24px',
            padding: '8px 12px 0'
          },
          '&.SmBottomSheet-expanded .SmBottomSheet-header': {
            // Close button at the inline end (left in RTL), where dialog close buttons usually sit.
            justifyContent: 'flex-end'
          },
          '& .SmBottomSheet-handle': {
            width: '40px',
            height: '4px',
            borderRadius: '2px',
            backgroundColor: theme.palette.divider
          },
          '& .SmBottomSheet-body': {
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overscrollBehavior: 'contain'
          },
          // A <form> wrapping DialogContent + DialogActions: keep the content scrolling and the actions pinned.
          '& .SmBottomSheet-body > form': {
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }
    }
  };
}
